import React, { useEffect, useState } from "react";
import { Brain } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import api from "../lib/api";
import { useI18n } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";

const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");
const Card = ({ id, label, value }) => (
    <div data-testid={`al-stat-${id}`} className="rounded-lg border border-[#1e293b] bg-[#0b0f17] px-3 py-2">
        <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-xl font-mono font-bold text-slate-100">{value ?? "—"}</div>
    </div>
);

function History({ rows, t }) {
    if (!rows.length) return <div data-testid="al-empty" className="text-sm text-slate-500 py-8 text-center">{t("al_empty")}</div>;
    return (
        <div className="max-h-[60vh] overflow-auto border border-[#1e293b] rounded-md">
            <table data-testid="al-history-table" className="w-full text-sm">
                <thead className="sticky top-0 bg-[#0b0f17] text-[11px] font-mono uppercase text-slate-400">
                    <tr>{["al_field", "al_ai_read", "al_user_fixed", "al_times", "al_last", "al_where"].map((k) => <th key={k} className="text-left px-2 py-2">{t(k)}</th>)}</tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} data-testid={`al-row-${r.id}`} className="border-t border-[#182232]">
                            <td className="px-2 py-1.5 font-mono text-slate-300">{t(`ai_f_${r.field}`)}</td>
                            <td className="px-2 py-1.5 text-rose-300 line-through">{r.ai_value || <span className="no-underline text-slate-500 italic">({t("al_k_missed")})</span>}</td>
                            <td className="px-2 py-1.5 text-emerald-300">{r.reassign_to ? `→ ${t(`ai_f_${r.reassign_to}`)}` : r.correct_value || <span className="text-slate-500 italic">({t("al_k_removed")})</span>}</td>
                            <td className={`px-2 py-1.5 font-mono ${r.count > 1 ? "text-amber-300 font-bold" : "text-slate-300"}`}>{r.count}×</td>
                            <td className="px-2 py-1.5 text-xs text-slate-400">{fmt(r.last_at)}{r.last_user ? ` · ${r.last_user}` : ""}</td>
                            <td className="px-2 py-1.5 text-xs text-slate-500">{[r.file_type, r.drawing_type, r.location].filter(Boolean).join(" · ")}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Admin({ s, t }) {
    if (!s) return null;
    const maxRate = Math.max(1, ...s.trend.map((w) => w.error_rate || 0));
    return (
        <div data-testid="al-admin" className="space-y-4 max-h-[62vh] overflow-auto pr-1">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <Card id="analyses" label={t("al_p_analyses")} value={s.progress.analyses} />
                <Card id="rules" label={t("al_p_rules")} value={s.progress.rules} />
                <Card id="corrections" label={t("al_p_corrections")} value={s.progress.corrections} />
                <Card id="confirms" label={t("al_p_confirms")} value={s.progress.confirms} />
                <Card id="confirmed-values" label={t("al_p_confirmed_values")} value={s.progress.confirmed_values} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                <section>
                    <h3 className="text-xs font-mono uppercase text-slate-400 mb-1.5">{t("al_top_errors")}</h3>
                    {s.top_errors.map((e, i) => (
                        <div key={i} data-testid={`al-top-${i}`} className="text-sm flex gap-2 py-0.5">
                            <span className="font-mono text-amber-300 w-8">{e.count}×</span>
                            <span className="text-slate-400">{t(`ai_f_${e.field}`)}:</span>
                            <span className="text-rose-300 line-through truncate">{e.ai_value}</span>→<span className="text-emerald-300 truncate">{e.correct_value}</span>
                        </div>
                    ))}
                </section>
                <section>
                    <h3 className="text-xs font-mono uppercase text-slate-400 mb-1.5">{t("al_top_fields")}</h3>
                    {s.by_field.map((f) => (
                        <div key={f.field} data-testid={`al-field-${f.field}`} className="flex items-center gap-2 text-sm py-0.5">
                            <span className="w-24 text-slate-300">{t(`ai_f_${f.field}`)}</span>
                            <div className="flex-1 h-2 rounded bg-[#182232]"><div className="h-2 rounded bg-rose-500" style={{ width: `${(100 * f.count) / (s.by_field[0]?.count || 1)}%` }} /></div>
                            <span className="font-mono text-xs text-slate-400 w-8 text-right">{f.count}</span>
                        </div>
                    ))}
                </section>
            </div>
            <section>
                <h3 className="text-xs font-mono uppercase text-slate-400 mb-1.5">{t("al_accuracy")}</h3>
                <div className="flex gap-2 flex-wrap">
                    {s.accuracy.length === 0 && <span data-testid="al-acc-empty" className="text-sm text-slate-500">{t("al_no_data")}</span>}
                    {s.accuracy.map((a) => (
                        <div key={a.file_type} data-testid={`al-acc-${a.file_type}`} className="rounded border border-[#1e293b] px-3 py-1.5 text-sm">
                            <span className="font-mono uppercase text-slate-400 mr-2">{a.file_type}</span>
                            <span className="font-bold text-emerald-300">{a.accuracy ?? "—"}%</span>
                            <span className="text-xs text-slate-500 ml-2">{t("al_acc_detail", { f: a.fields, c: a.corrections, a: a.analyses })}</span>
                        </div>
                    ))}
                </div>
            </section>
            <section>
                <h3 className="text-xs font-mono uppercase text-slate-400 mb-1.5">{t("al_trend")}</h3>
                <div data-testid="al-trend" className="flex items-end gap-1.5 h-28 border-b border-[#1e293b]">
                    {s.trend.map((w) => (
                        <div key={w.week} className="flex flex-col items-center justify-end h-full" title={`${w.week}: ${w.error_rate ?? 0}% (${w.corrections}/${w.fields})`}>
                            <span className="text-[9px] font-mono text-slate-400">{w.error_rate == null ? "n/a" : `${w.error_rate}%`}</span>
                            <div className="w-6 rounded-t bg-blue-500" style={{ height: `${Math.max(2, (80 * (w.error_rate || 0)) / maxRate)}%` }} />
                            <span className="text-[9px] font-mono text-slate-500 mt-0.5">{w.week.slice(5)}</span>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}

export function AiLearningDialog({ open, onOpenChange }) {
    const { t } = useI18n();
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [stats, setStats] = useState(null);
    const isAdmin = user?.role === "admin";
    useEffect(() => {
        if (!open) return;
        api.get("/ai/corrections").then(({ data }) => setRows(data)).catch(() => setRows([]));
        if (isAdmin) api.get("/ai/admin-stats").then(({ data }) => setStats(data)).catch(() => setStats(null));
    }, [open, isAdmin]);
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="ai-learning-dialog" className="max-w-5xl bg-[#111827] border-[#1e293b] text-slate-100">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-mono"><Brain className="w-4 h-4 text-fuchsia-400" /> {t("al_title")}</DialogTitle>
                    <DialogDescription className="text-slate-400">{t("al_desc")}</DialogDescription>
                </DialogHeader>
                <Tabs defaultValue="history">
                    <TabsList className="bg-[#0b0f17]">
                        <TabsTrigger data-testid="al-tab-history" value="history">{t("al_tab_history")}</TabsTrigger>
                        {isAdmin && <TabsTrigger data-testid="al-tab-admin" value="admin">{t("al_tab_admin")}</TabsTrigger>}
                    </TabsList>
                    <TabsContent value="history"><History rows={rows} t={t} /></TabsContent>
                    {isAdmin && <TabsContent value="admin"><Admin s={stats} t={t} /></TabsContent>}
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}
